import {
  DEFAULT_CATEGORY,
  DEFAULT_PARAM_ARRAY_COUNT,
  DEFAULT_SUBCATEGORY,
  DEVICE_PARAM_TYPE,
  INTEGRATIONS,
  LENS_USER_TYPE,
  LINE,
  LINE_LIMIT_PARAM,
  MAIN,
  MS_INTEGRATIONS_SETTINGS_NAME,
  NO_GROUPING,
  PARAM_ARRAY_PLACEHOLDER,
  PARAM_TYPE,
  SERVER,
  SERVER_SETTINGS_NAME,
  TEAMS
} from "Constants";
import { useFeatureFlagWithTenant } from "elements";
import {
  ConfigurationAttribute,
  DeviceParameterFragment,
  ExtendedDeviceParameterFragment,
  Maybe,
  Param2DArrayItem,
  ParamArrayItem,
  PolicyDeploymentScope,
  SwSupportUpdatePathFragment,
  useDeviceParametersQuery
} from "generated/graphql";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import { useMemo } from "react";
import { isLines } from "../utils/provisionedDeviceSettingsUtils";

export type SwSupportUpdatePath = Omit<
  SwSupportUpdatePathFragment,
  "__typename"
>;
export type SettingValue = string | number | boolean | SwSupportUpdatePath[];

export type SettingsValues = {
  [key: string]: SettingValue;
};

export type Setting = DeviceParameterFragment & {
  category: string;
  subcategory: string;
  settingName: string;
  value: SettingValue;
  index1?: number;
  index2?: number;
  policyDeploymentScope: PolicyDeploymentScope | null;
};

export type SettingsTree = {
  [category: string]: {
    [subcategory: string]: {
      [collection: string]: {
        [grouping: string]:
          | {
              [subGrouping: string]: Setting[];
            }
          | Setting[];
      };
    };
  };
};

export type NumOfLines = {
  regLimit: number;
  maxConfiguredLine: number;
};

const getNumOfLines = (
  params: Maybe<ExtendedDeviceParameterFragment>[]
): NumOfLines => {
  let maxConfiguredLine = 0;
  let regLimit = 0;
  params.forEach(param => {
    if (!param || !param.metadata) {
      return;
    }

    if (param.name === LINE_LIMIT_PARAM) {
      regLimit = parseInt(
        param?.policyAttribute?.currentValue ||
          param?.metadata?.defaultValue ||
          "0"
      );
    } else {
      const { categories, paramType } = param.metadata;
      const [category, subcategory] = categories ?? [];
      // Need to figure out what is maximum configured Line (line number)
      // To do so, check policy values of every parameter belonging to Call Configuration/Lines,
      // and take note of highest line number appearing in replace1 property (which identifies line number)
      if (isLines(category, subcategory)) {
        let valuesToCheck: (
          | Maybe<ParamArrayItem>
          | Maybe<Param2DArrayItem>
        )[] = [];
        switch (paramType) {
          case PARAM_TYPE.PARAM_ARRAY:
            valuesToCheck = param.policyAttribute?.paramArray ?? [];
            break;
          case PARAM_TYPE.PARAM_2D_ARRAY:
            valuesToCheck = param.policyAttribute?.param2DArray ?? [];
            break;
        }

        valuesToCheck.forEach(value => {
          const lineNum = value?.replace1 ?? 0;
          if (lineNum > maxConfiguredLine) {
            maxConfiguredLine = lineNum;
          }
        });
      }
    }
  });

  return {
    regLimit,
    maxConfiguredLine
  };
};

export const computeParamArrayName = (name: string, replacements: number[]) =>
  replacements.reduce(
    (computed, replacement, index) =>
      computed.replace(
        new RegExp(PARAM_ARRAY_PLACEHOLDER + (index + 1), "i"),
        replacement.toString()
      ),
    name
  );

const getParam = <T extends ParamArrayItem | Param2DArrayItem>(
  name: string,
  paramArray: (T | null)[],
  predicate: (value: T) => boolean,
  replacements: number[],
  defaultValue: string | null | undefined,
  policyDeploymentScope: Maybe<PolicyDeploymentScope>
) => {
  const value =
    paramArray.filter(item => item && predicate(item))[0]?.currentValue ?? "";
  return {
    name: computeParamArrayName(name, replacements),
    value,
    defaultValue,
    policyDeploymentScope
  };
};

const setValue = (
  values: SettingsValues,
  defaultValues: SettingsValues,
  name: string,
  policyAttribute: Maybe<ConfigurationAttribute>,
  settingMetadata: DeviceParameterFragment,
  policyDeploymentScope: Maybe<PolicyDeploymentScope>,
  currentScope: PolicyDeploymentScope
) => {
  const {
    type,
    paramType,
    defaultValue,
    numReplace1Max,
    paramArray,
    numReplace2Max,
    param2DArray
  } = settingMetadata;

  let params = [];
  if (
    paramType === PARAM_TYPE.PARAM_ARRAY ||
    paramType === PARAM_TYPE.PARAM_2D_ARRAY
  ) {
    const max1 = numReplace1Max || DEFAULT_PARAM_ARRAY_COUNT;
    const max2 = numReplace2Max || DEFAULT_PARAM_ARRAY_COUNT;
    for (let index1 = 1; index1 <= max1; index1++) {
      if (paramType === PARAM_TYPE.PARAM_2D_ARRAY) {
        for (let index2 = 1; index2 <= max2; index2++) {
          params.push(
            getParam<Param2DArrayItem>(
              name,
              policyAttribute?.param2DArray || [],
              param => param.replace1 === index1 && param.replace2 === index2,
              [index1, index2],
              param2DArray?.[index1 - 1]?.[index2 - 1],
              policyDeploymentScope
            )
          );
        }
      } else {
        params.push(
          getParam<ParamArrayItem>(
            name,
            policyAttribute?.paramArray || [],
            param => param.replace1 === index1,
            [index1],
            paramArray?.[index1 - 1],
            policyDeploymentScope
          )
        );
      }
    }
  } else {
    params.push({
      name,
      value: policyAttribute?.currentValue,
      defaultValue,
      policyDeploymentScope
    });
  }

  params.forEach(param => {
    const defaultValue = param.defaultValue ?? "";
    let value: SettingValue = "";
    if (
      currentScope &&
      (param.policyDeploymentScope === currentScope ||
        currentScope === PolicyDeploymentScope.Device)
    ) {
      value = param.value ?? "";
    }

    switch (type) {
      case DEVICE_PARAM_TYPE.BOOL:
        value = (value || defaultValue) === "1";
        break;
      case DEVICE_PARAM_TYPE.XBOOL:
        value = (value || defaultValue) === "True";
        break;
      case DEVICE_PARAM_TYPE.UINT:
      case DEVICE_PARAM_TYPE.UINT_NULL:
      case DEVICE_PARAM_TYPE.SINT:
      case DEVICE_PARAM_TYPE.SINT_NULL:
        // in some cases value might not be a number, even if type suggests that (e.g. "")
        // in those cases leave value as is
        const parsedValue = parseInt(`${value}`);
        if (!isNaN(parsedValue)) {
          value = parsedValue;
        }
        break;
    }

    values[param.name] = value;
    defaultValues[param.name] = defaultValue;
  });
};

const putSetting = (
  settings: Setting[],
  setting: Setting,
  groupingOrder: number | null
) => {
  if (groupingOrder) {
    // grouping order starts from 1
    settings[groupingOrder - 1] = setting;
  } else {
    settings.push(setting);
  }
};

const addSetting = (
  settingsTree: SettingsTree,
  values: SettingsValues,
  category: string,
  subcategory: string,
  collection: string,
  grouping: string,
  name: string,
  metadata: DeviceParameterFragment,
  policyDeploymentScope: PolicyDeploymentScope | null,
  groupingOrder: number | null,
  paramArrayIndexes: number[] = [],
  subGrouping: string = ""
) => {
  const settingsBySubcategory = (settingsTree[category] =
    settingsTree[category] || {});
  const settingsByCollection = (settingsBySubcategory[subcategory] =
    settingsBySubcategory[subcategory] || {});
  const settingsByGroup = (settingsByCollection[collection] =
    settingsByCollection[collection] || {});

  let settings: Setting[];
  if (subGrouping) {
    let settingsBySubgroup = (settingsByGroup[grouping] =
      settingsByGroup[grouping] || {}) as { [subGrouping: string]: Setting[] };
    settings = settingsBySubgroup[subGrouping] =
      settingsBySubgroup[subGrouping] || [];
  } else {
    settings = (settingsByGroup[grouping] =
      settingsByGroup[grouping] || []) as Setting[];
  }

  let settingName = name;
  let lensConditional = metadata.lensConditional ?? "";
  if (paramArrayIndexes.length) {
    settingName = computeParamArrayName(name, paramArrayIndexes);
    lensConditional = computeParamArrayName(lensConditional, paramArrayIndexes);
  }

  const setting: Setting = {
    ...metadata,
    category,
    subcategory,
    settingName,
    value: values[settingName],
    lensConditional,
    index1: paramArrayIndexes[0],
    index2: paramArrayIndexes[1],
    policyDeploymentScope
  };

  putSetting(settings, setting, groupingOrder);
};

export const useProvisionedDeviceSettings = (
  deviceModel: string,
  policyScope: PolicyDeploymentScope,
  deviceId?: string,
  siteId?: string,
  groupId?: string
) => {
  const tenantId = useTenantId();
  const [isTACIntegrationEnabled] = useFeatureFlagWithTenant("TAC_INTEGRATION");
  const { data, fetching } = useDeviceParametersQuery({
    variables: {
      tenantId,
      deviceId,
      deviceModel,
      siteId,
      groupId,
      sendOnlyChanged: false
    }
  });

  return useMemo(() => {
    let deviceSettings: SettingsTree = {};
    let values: SettingsValues = {};
    let defaultValues: SettingsValues = {};
    // used in specific case, for category "Call Configuration" and subcategory "Lines"
    let numOfLines: NumOfLines = {
      regLimit: -1,
      maxConfiguredLine: -1
    };
    let msTeamsAssistedProvisioningEnabled = false;

    if (fetching || !data?.getDeviceParametersExtended) {
      return {
        deviceParameters: [],
        deviceSettings,
        numOfLines,
        values,
        defaultValues,
        msTeamsAssistedProvisioningEnabled,
        fetching
      };
    }

    const extendedDeviceParameters = data.getDeviceParametersExtended;
    extendedDeviceParameters.forEach(extendedDeviceParameter => {
      if (!extendedDeviceParameter?.metadata) {
        return;
      }

      const {
        name,
        paramType,
        categories,
        lensUserType
      } = extendedDeviceParameter.metadata;

      if (!name) {
        return;
      }

      let [category, subcategory] = categories ?? [];
      // if Teams Admin Center integration feature is disabled do not display related settings
      if (
        category === INTEGRATIONS &&
        subcategory === TEAMS &&
        !isTACIntegrationEnabled
      ) {
        return;
      }

      const settingName = name.toLowerCase();
      setValue(
        values,
        defaultValues,
        settingName,
        extendedDeviceParameter.policyAttribute,
        extendedDeviceParameter.metadata,
        extendedDeviceParameter.policyDeploymentScope,
        policyScope
      );

      if (name === MS_INTEGRATIONS_SETTINGS_NAME) {
        msTeamsAssistedProvisioningEnabled =
          extendedDeviceParameter.policyAttribute?.currentValue === "0"
            ? false
            : true;
      }

      // ignore parameters which are not lensUserType=1 (SIMPLE_USER) and not set in a policy (policyAttribute is null)
      if (
        lensUserType !== LENS_USER_TYPE.SIMPLE_USER &&
        extendedDeviceParameter.policyAttribute === null
      ) {
        return;
      }

      let { grouping, groupingOrder } = extendedDeviceParameter.metadata;
      category = category || DEFAULT_CATEGORY;
      subcategory = subcategory || DEFAULT_SUBCATEGORY;
      grouping = grouping || NO_GROUPING;

      // categories/subcategories might contain "/"
      // that can mess with routing so replace "/" with "|"
      category = category.replace(/\//g, "|");
      subcategory = subcategory.replace(/\//g, "|");

      // need special handling for these, at least for now
      const isLinesParam = isLines(category, subcategory);

      if (
        paramType === PARAM_TYPE.PARAM_ARRAY ||
        paramType === PARAM_TYPE.PARAM_2D_ARRAY
      ) {
        const collectionNameBase = isLinesParam ? LINE : subcategory;
        if (isLinesParam && numOfLines.regLimit === -1) {
          numOfLines = getNumOfLines(extendedDeviceParameters);
        }
        const max1 =
          (isLinesParam
            ? Math.max(numOfLines.maxConfiguredLine, numOfLines.regLimit)
            : null) ||
          extendedDeviceParameter.metadata.numReplace1Max ||
          DEFAULT_PARAM_ARRAY_COUNT;

        for (let index1 = 1; index1 <= max1; index1++) {
          const collectionName = `${collectionNameBase} ${index1}`;

          if (paramType === PARAM_TYPE.PARAM_ARRAY) {
            const paramArray =
              extendedDeviceParameter.policyAttribute?.paramArray;
            const policyDeploymentScope = paramArray?.some(
              param => param?.replace1 === index1
            )
              ? extendedDeviceParameter.policyDeploymentScope
              : null;
            addSetting(
              deviceSettings,
              values,
              category,
              subcategory,
              collectionName,
              grouping,
              settingName,
              extendedDeviceParameter.metadata,
              policyDeploymentScope,
              groupingOrder,
              [index1]
            );
          } else {
            let groupingNameBase = grouping;
            let subGrouping = "";
            if (isLinesParam && name.includes(SERVER_SETTINGS_NAME)) {
              groupingNameBase = SERVER;
              subGrouping = grouping;
            }

            const max2 =
              extendedDeviceParameter.metadata.numReplace2Max ||
              DEFAULT_PARAM_ARRAY_COUNT;

            for (let index2 = 1; index2 <= max2; index2++) {
              const groupingName = `${groupingNameBase} ${index2}`;

              const param2DArray =
                extendedDeviceParameter.policyAttribute?.param2DArray;
              const policyDeploymentScope = param2DArray?.some(
                param =>
                  param?.replace1 === index1 && param?.replace2 === index2
              )
                ? extendedDeviceParameter.policyDeploymentScope
                : null;
              addSetting(
                deviceSettings,
                values,
                category,
                subcategory,
                collectionName,
                groupingName,
                settingName,
                extendedDeviceParameter.metadata,
                policyDeploymentScope,
                groupingOrder,
                [index1, index2],
                subGrouping
              );
            }
          }
        }
      } else {
        addSetting(
          deviceSettings,
          values,
          category,
          subcategory,
          MAIN,
          grouping,
          settingName,
          extendedDeviceParameter.metadata,
          extendedDeviceParameter.policyDeploymentScope,
          groupingOrder
        );
      }
    });

    // We need to do some additional processing
    // For PARAM_ARRAY settings we should check if same grouping is present in MAIN collection
    // If it is, then move setting there. After moving delete grouping/collection if there are no more settings present
    Object.values(deviceSettings)
      .flatMap(bySubcategory => Object.values(bySubcategory))
      .forEach(settingsByCollection => {
        Object.entries(settingsByCollection).forEach(
          ([collection, settingsByGroup]) => {
            if (collection === MAIN) {
              return;
            }
            Object.entries(settingsByGroup).forEach(([grouping, settings]) => {
              if (!Array.isArray(settings)) {
                return;
              }
              // check if corresponding grouping is present in MAIN collection of settings
              const mainCollectionSettings =
                settingsByCollection[MAIN]?.[grouping];

              if (
                !mainCollectionSettings ||
                !Array.isArray(mainCollectionSettings)
              ) {
                return;
              }

              // check if all settings in current grouping are of type PARAM_ARRAY (no PARAM_2D_ARRAY present)
              const nonNullSettings = settings.filter(
                setting => setting !== null
              );
              const isAllParamArray = nonNullSettings.every(
                setting => setting.paramType === PARAM_TYPE.PARAM_ARRAY
              );

              if (!isAllParamArray) {
                return;
              }

              nonNullSettings.forEach(setting => {
                const { groupingOrder, index1 } = setting;
                let order = 0;
                if (groupingOrder && index1) {
                  order = groupingOrder + index1 - 1;
                }
                if (setting.friendlyName) {
                  setting.friendlyName += " " + index1;
                }

                putSetting(mainCollectionSettings, setting, order);
              });

              delete settingsByGroup[grouping];
            });

            if (Object.keys(settingsByGroup).length === 0) {
              delete settingsByCollection[collection];
            }
          }
        );
      });

    return {
      deviceParameters: data?.getDeviceParametersExtended ?? [],
      deviceSettings,
      numOfLines,
      values,
      defaultValues,
      msTeamsAssistedProvisioningEnabled,
      fetching
    };
  }, [
    fetching,
    isTACIntegrationEnabled,
    data?.getDeviceParametersExtended,
    policyScope
  ]);
};
